/**
 * Local interface primitives.
 *
 * Deliberately small and local rather than a component library: the dashboard needs six shapes,
 * and a runtime dependency on a design system would be a larger commitment than the surface
 * justifies. Shared tokens come from `@urlshot/design-tokens`; anything reused by both the
 * dashboard and the marketing site moves to `@urlshot/ui`.
 */
import type {
  HTMLAttributes,
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  Ref,
} from 'react';
import { cn } from '@urlshot/ui';

type ButtonVariant = 'primary' | 'secondary' | 'danger';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  /** The logo green with dark text, as the logo itself is drawn; white text on it is unreadable. */
  primary:
    'bg-brand-400 text-neutral-900 hover:bg-brand-500 disabled:bg-brand-100 disabled:text-neutral-500',
  secondary:
    'border border-neutral-300 bg-white text-neutral-900 hover:bg-neutral-50 disabled:text-neutral-400',
  danger: 'border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:text-red-300',
};

/**
 * A button's look, for the rare link that must look like one -- "Continue with Google" navigates
 * away from the application, so it is a link, not a button with a click handler.
 */
export function buttonClasses(variant: ButtonVariant = 'primary', className?: string): string {
  return cn(
    'inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold',
    'transition-colors disabled:cursor-not-allowed',
    // A visible focus ring is not decoration: keyboard users cannot operate the form without it.
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-900',
    BUTTON_VARIANTS[variant],
    className,
  );
}

export function Button({
  variant = 'primary',
  className,
  ref,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  /** React 19 passes `ref` as a plain prop; no `forwardRef` wrapper is needed. */
  ref?: Ref<HTMLButtonElement>;
}) {
  return <button ref={ref} {...props} className={buttonClasses(variant, className)} />;
}

/**
 * A choice between a few views of the same thing: a billing interval, a time range.
 *
 * Not a row of `Button`s. Choosing how to look at something is not an action, and a filled button
 * for the current option reads like a call to action beside the page's real ones. One bordered group;
 * the selected option is tinted a light green, the others are plain.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  /** Names the group for assistive technology. */
  label: string;
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div
      className="inline-flex rounded-full border border-neutral-300 bg-white p-1 text-sm"
      role="group"
      aria-label={label}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
          className={cn(
            'rounded-full px-3 py-1.5 font-medium transition-colors',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-900',
            option.value === value
              ? 'bg-brand-200 text-neutral-900'
              : 'text-neutral-700 hover:bg-neutral-50',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Field({
  label,
  error,
  hint,
  children,
  htmlFor,
}: {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  children: ReactNode;
  htmlFor: string;
}) {
  const errorId = `${htmlFor}-error`;
  const hintId = `${htmlFor}-hint`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-neutral-900">
        {label}
      </label>
      {children}
      {hint !== undefined && (
        <p id={hintId} className="text-xs text-neutral-500">
          {hint}
        </p>
      )}
      {/* `role="alert"` so a screen reader announces a validation failure rather than leaving the
          user to discover it by tabbing back through the form. */}
      {error !== undefined && (
        <p id={errorId} role="alert" className="text-xs text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}

export function TextInput({
  className,
  invalid,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  return (
    <input
      {...props}
      aria-invalid={invalid === true ? 'true' : undefined}
      className={cn(
        'rounded-md border bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-neutral-900',
        invalid === true ? 'border-red-400' : 'border-neutral-300',
        className,
      )}
    />
  );
}

/** Non-field error, such as a rejected login or an unreachable service. */
export function FormError({ message }: { message: string | null }) {
  if (message === null) return null;
  return (
    <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
      {message}
    </p>
  );
}

/**
 * Surface container.
 *
 * Forwards any remaining props to the underlying element. Without that, a `data-testid` placed on
 * a Card is silently dropped -- the element renders, the attribute does not, and a test looking for
 * it fails while the page is entirely correct.
 */
export function Card({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLElement> & { children: ReactNode; className?: string }) {
  return (
    <section
      {...rest}
      className={cn('rounded-lg border border-neutral-200 bg-white p-5', className)}
    >
      {children}
    </section>
  );
}

/**
 * Tone per status.
 *
 * Covers both vocabularies this dashboard shows -- API-key lifecycle and request outcome -- because
 * a badge that fell back to one colour would render `succeeded` and `failed` identically, which is
 * precisely the distinction a reader is scanning for.
 */
const STATUS_TONES: Record<string, string> = {
  active: 'bg-green-50 text-green-800',
  succeeded: 'bg-green-50 text-green-800',
  revoked: 'bg-neutral-100 text-neutral-600',
  failed: 'bg-red-50 text-red-800',
  rejected: 'bg-red-50 text-red-800',
  throttled: 'bg-amber-50 text-amber-800',
};

export function StatusBadge({ status }: { status: string }) {
  const tone = STATUS_TONES[status] ?? 'bg-amber-50 text-amber-800';
  return <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', tone)}>{status}</span>;
}

/** Placeholder for a route whose functionality arrives in a later phase. */
export function PhasePlaceholder({ title, arrives }: { title: string; arrives: string }) {
  return (
    <Card>
      <h2 className="text-lg font-semibold text-neutral-900">{title}</h2>
      <p className="mt-2 text-sm text-neutral-600">{arrives}</p>
    </Card>
  );
}
